import { useState } from "react";
import React, { forwardRef } from "react";
import { BASE_URL } from "../../Global.js";
import { SendCareerFormData } from "../../Services/partnerService.js";
import { FormField } from "../ui/Form";
import Button from "../ui/Button/Button";
import { useFormValidation } from "../../Hooks/useFormValidation.js";
import { careerSchema } from "../../schemas/careerSchema";
import { useTranslation } from "react-i18next";

const FinalSection = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    linkedinAccount: "",
    whyCiqler: "",
  });

  const [formError, setFormError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const { errors, validate, validateField } = useFormValidation(careerSchema);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationResult = validate(formData);

    if (!validationResult.success) {
      setFormError(t("common.fixFormErrors"));
      return;
    }

    setFormError("");
    setIsButtonDisabled(true);

    try {
      const response = await SendCareerFormData(formData);

      if (response.status === 200) {
        setFormData({
          firstName: "",
          lastName: "",
          linkedinAccount: "",
          whyCiqler: "",
        });
        setIsSubmitted(true);
        setIsButtonDisabled(false);

        setTimeout(() => {
          setIsSubmitted(false);
        }, 1500);
      } else {
        throw new Error("Failed to submit form data");
      }
    } catch (error) {
      setFormError("Failed to submit form data");
      setIsSubmitted(false);
      setIsButtonDisabled(false);
    }
  };

  return (
    <section
      className="w-full py-12 md:py-24 lg:py-32 bg-red-400 rounded-t-[20px] "
      id="finalSection"
      ref={ref}
    >
      <div className="container mx-auto px-8 md:px-6 flex flex-col lg:flex-row gap-8">
        <div className="lg:w-1/2">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-grey-500">
            {t("about.joinCiqlerTitle")}
          </h2>
          <p className="text-lg text-grey-500 mt-4">
            {t("about.joinCiqlerDesc")}
          </p>
        </div>

        <div
          ref={ref}
          className="bg-white rounded-lg shadow-md p-6 space-y-6 max-w-xl w-full"
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <FormField
              label={t("forms.firstName")}
              htmlFor="firstName"
              required
              error={errors.firstName}
              inputProps={{
                id: "firstName",
                placeholder: "John",
                value: formData.firstName,
                onChange: (e) => handleChange("firstName", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("forms.lastName")}
              htmlFor="lastName"
              required
              error={errors.lastName}
              inputProps={{
                id: "lastName",
                placeholder: "Doe",
                value: formData.lastName,
                onChange: (e) => handleChange("lastName", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("career.linkedinAccount")}
              htmlFor="linkedinAccount"
              required
              error={errors.linkedinAccount}
              inputProps={{
                id: "linkedinAccount",
                placeholder: "https://www.linkedin.com/in/yourprofile",
                value: formData.linkedinAccount,
                onChange: (e) =>
                  handleChange("linkedinAccount", e.target.value),
                variant: "box",
              }}
            />
            <FormField
              label={t("career.whyJoinQuestion")}
              htmlFor="whyCiqler"
              type="textarea"
              error={errors.whyCiqler}
              inputProps={{
                id: "whyCiqler",
                placeholder: t("forms.detailsPlaceholder"),
                className: "h-32",
                value: formData.whyCiqler,
                onChange: (e) => handleChange("whyCiqler", e.target.value),
                variant: "box",
              }}
            />

            <Button
              type="submit"
              disabled={isButtonDisabled}
              variant="danger"
              fullWidth
              className={
                isButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
              }
            >
              {isButtonDisabled ? t("common.submitting") : t("common.submit")}
            </Button>
            {formError && <p className="text-red-500 text-sm">{formError}</p>}
            {isSubmitted && (
              <p className="text-green-500 text-sm">
                {t("form.registerSuccess")}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
});

export default FinalSection;
